import { useState, useCallback } from 'react';
import { Contact } from '../types';
import { apiFetch } from '../../../api/core';
import { useCRMContext } from '../CRMContext';
import { useContactAttributesConfig } from './useContactAttributesConfig';

export type FieldSelection = 'target' | 'source' | 'both';

// Allow dynamic keys for custom attributes
export type MergeSelections = Record<string, FieldSelection>;

const defaultBaseSelections: MergeSelections = {
  first_name: 'target',
  last_name: 'target',
  middle_name: 'target',
  gender: 'target',
  birth_date: 'target',
  description: 'both', 
  tags: 'both',
  categories: 'both',
  emails: 'both',
  phones: 'both',
  geo_country: 'both',
  geo_city: 'both',
  consent_pd: 'target',
  consent_emails: 'target',
  communication_language: 'both',
};

export const useContactMerge = (onComplete?: (result: Contact) => void) => {
  const [targetContact, setTargetContact] = useState<Contact | null>(null);
  const [sourceContact, setSourceContact] = useState<Contact | null>(null);
  const [selections, setSelections] = useState<MergeSelections>(defaultBaseSelections);
  const [isMerging, setIsMerging] = useState(false);

  const { apiPath } = useCRMContext();
  const { attributes: customAttributes } = useContactAttributesConfig();

  // Generate complete default selections including custom attributes
  const getInitialSelections = useCallback(() => {
    const sel = { ...defaultBaseSelections };
    customAttributes.forEach(attr => {
      // Bools cannot be merged; texts can be concatenated
      sel[attr.code] = attr.type === 'bool' ? 'target' : 'both';
    });
    return sel;
  }, [customAttributes]);

  const startMerge = useCallback((contact: Contact) => {
    setTargetContact(contact);
    setSourceContact(null);
    setSelections(getInitialSelections());
  }, [getInitialSelections]);

  const addSource = useCallback((contact: Contact) => {
    if (targetContact?._id === contact._id) return;
    
    setSourceContact(contact);
    
    setSelections(prev => {
      const newSel = { ...prev };
      
      const strictScalarFields = [
        'first_name', 'last_name', 'middle_name', 'gender', 'birth_date', 'consent_pd', 'consent_emails'
      ];
      const customBools = customAttributes.filter(a => a.type === 'bool').map(a => a.code);
      
      [...strictScalarFields, ...customBools].forEach(field => {
        const tVal = targetContact?.attributes?.[field] ?? (targetContact as any)?.[field];
        const sVal = contact?.attributes?.[field] ?? (contact as any)?.[field];
        
        if (targetContact && !tVal && sVal) {
          newSel[field] = 'source';
        } else {
          newSel[field] = 'target';
        }
      });

      newSel.description = 'both';
      newSel.tags = 'both';
      newSel.categories = 'both';
      newSel.emails = 'both';
      newSel.phones = 'both';
      newSel.geo_country = 'both';
      newSel.geo_city = 'both';
      newSel.communication_language = 'both';
      
      // Default custom text fields to both
      customAttributes.filter(a => a.type !== 'bool').forEach(attr => {
        newSel[attr.code] = 'both';
      });

      return newSel;
    });
  }, [targetContact, customAttributes]);

  const setFieldSelection = useCallback((field: string, value: FieldSelection) => {
    setSelections(prev => ({ ...prev, [field]: value }));
  }, []);

  const swapContacts = useCallback(() => {
    setTargetContact(sourceContact);
    setSourceContact(targetContact);
    
    setSelections(prev => {
      const newSel = { ...prev };
      Object.keys(newSel).forEach(k => {
        if (newSel[k] === 'target') newSel[k] = 'source';
        else if (newSel[k] === 'source') newSel[k] = 'target';
      });
      return newSel;
    });
  }, [targetContact, sourceContact]);

  const cancelMerge = useCallback(() => {
    setTargetContact(null);
    setSourceContact(null);
    setIsMerging(false);
  }, []);

  const isDialogVisible = targetContact !== null && sourceContact !== null;
  const isMergeStarted = targetContact !== null;

  const executeMerge = useCallback(async () => {
    if (!targetContact || !sourceContact) return;

    setIsMerging(true);

    const payload: Partial<Contact> & { merge_target_id: string; merge_source_id: string, attributes?: Record<string, any> } = {
      merge_target_id: targetContact._id,
      merge_source_id: sourceContact._id,
      attributes: {} // Initialize for custom fields
    };

    const getVal = (field: string, isCollection = false, isTextJoin = false) => {
      const sel = selections[field];
      
      // Look in both root and nested attributes map
      const getContactVal = (contact: Contact) => contact.attributes?.[field] ?? (contact as any)[field];
      
      if (sel === 'both') {
        if (isTextJoin) {
          const tVal = getContactVal(targetContact) || '';
          const sVal = getContactVal(sourceContact) || '';
          if (tVal && sVal && tVal !== sVal) return `${tVal}\n\n${sVal}`;
          return tVal || sVal || '';
        }
        
        if (isCollection) {
          const tArr = getContactVal(targetContact) || [];
          const sArr = getContactVal(sourceContact) || [];
          
          if (['tags', 'categories', 'geo_country', 'geo_city', 'communication_language'].includes(field)) {
            return Array.from(new Set([...tArr, ...sArr]));
          } else {
            const combined = [...tArr, ...sArr];
            const unique = [];
            const seen = new Set();
            for (const item of combined) {
              if (!seen.has(item.value)) {
                seen.add(item.value);
                unique.push(item);
              }
            }
            return unique;
          }
        }
      }
      
      return sel === 'target' ? getContactVal(targetContact) : getContactVal(sourceContact);
    };

    // Standard scalar fields
    payload.first_name = getVal('first_name');
    payload.last_name = getVal('last_name');
    payload.middle_name = getVal('middle_name');
    payload.gender = getVal('gender');
    payload.birth_date = getVal('birth_date');
    payload.consent_pd = getVal('consent_pd');
    payload.consent_emails = getVal('consent_emails');
    payload.description = getVal('description', false, true);
    
    // Standard collections
    payload.tags = getVal('tags', true);
    payload.categories = getVal('categories', true);
    payload.emails = getVal('emails', true);
    payload.phones = getVal('phones', true);
    payload.geo_country = getVal('geo_country', true);
    payload.geo_city = getVal('geo_city', true);
    payload.communication_language = getVal('communication_language', true);

    // Apply custom attributes
    customAttributes.forEach(attr => {
      payload.attributes![attr.code] = getVal(attr.code, false, attr.type !== 'bool');
    });

    if (Object.keys(payload.attributes!).length === 0) {
      delete payload.attributes;
    }

    try {
      const result = await apiFetch<Contact>(`${apiPath}/contact/merge`, 'POST', payload);
      cancelMerge();
      if(onComplete) {
        onComplete(result);
      }
    } finally {
      setIsMerging(false);
    }
  }, [targetContact, sourceContact, selections, apiPath, cancelMerge, onComplete, customAttributes]);

  return {
    targetContact, sourceContact, selections, isMerging,
    isDialogVisible, isMergeStarted, startMerge, addSource,
    setFieldSelection, swapContacts, cancelMerge, executeMerge,
  };
};

export type ContactMergeData = ReturnType<typeof useContactMerge>;